import { ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type PatronAccessCardProps = {
  suspended: boolean;
  canSuspend: boolean;
  canActivate: boolean;
  canEdit: boolean;
  autoRenewSelect: "default" | "on" | "off";
  actionLoading: boolean;
  onAutoRenewChange: (value: "default" | "on" | "off") => void;
  onSaveAutoRenew: () => void;
  onSuspend: () => void;
  onActivate: () => void;
};

export function PatronAccessCard({
  suspended,
  canSuspend,
  canActivate,
  canEdit,
  autoRenewSelect,
  actionLoading,
  onAutoRenewChange,
  onSaveAutoRenew,
  onSuspend,
  onActivate,
}: PatronAccessCardProps) {
  const showBorrowing = (suspended && canActivate) || (!suspended && canSuspend);

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {suspended ? (
            <ShieldAlert className="h-4 w-4 text-amber-600" />
          ) : (
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
          )}
          Account access
        </CardTitle>
        <p className="text-sm text-slate-500">
          Control borrowing access and this patron&apos;s auto-renewal preference.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {showBorrowing && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-900">Borrowing</p>
            {suspended
              ? canActivate && (
                  <Button onClick={onActivate} disabled={actionLoading} className="gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    Reactivate account
                  </Button>
                )
              : canSuspend && (
                  <Button variant="destructive" onClick={onSuspend} disabled={actionLoading}>
                    Suspend account
                  </Button>
                )}
          </div>
        )}

        {canEdit && (
          <div className="space-y-3 border-t border-slate-100 pt-5">
            <div className="space-y-1">
              <Label>Automated loan renewal</Label>
              <p className="text-sm text-slate-500">
                Overrides the library default for scheduled auto-renewal. Manual renew at the desk
                is separate.
              </p>
            </div>
            <Select
              value={autoRenewSelect}
              onValueChange={(v) => onAutoRenewChange(v as "default" | "on" | "off")}
              disabled={actionLoading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Use library default</SelectItem>
                <SelectItem value="on">Allow auto-renew</SelectItem>
                <SelectItem value="off">Disable auto-renew</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={onSaveAutoRenew} disabled={actionLoading}>
              Save preference
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
